import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import "./lib/sentry"; // Must be first — initializes Sentry before anything else
import "agent-react-devtools/connect"; // Must load before React — registers __REACT_DEVTOOLS_GLOBAL_HOOK__
import React, { useEffect } from "react";
import ReactDOM from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { TooltipProvider } from "@shared/client/components/ui/tooltip/tooltip";
import { Sentry } from "./lib/sentry";
import { identifyPosthog, resetPosthog } from "./lib/posthog";
import { registerEntrySuperProperties, track } from "./lib/analytics";
import { referrerKindFromUrl, routeEntityToEntryKind, utmParamsFromSearch, } from "./lib/entry-context";
import { useRouteEntity } from "./lib/urls";
import { AuthProvider, useAuth } from "./auth/auth-context";
import { setAuthTokenProvider } from "./api/base-query-api";
import { resetBootstrap } from "./api/bootstrap-api";
import { resetInitialBlocks } from "./api/initial-blocks-api";
import { clearBlockEnvelopesSnapshot, clearCollectionsSnapshot, clearStreamBlockIdsSnapshot, } from "./data/idb-persistence";
import { Routes } from "./routes";
import { LoginScreen } from "./screens/login-screen/login-screen";
import { queryClient } from "./data/query-client";
import { useStaleRefresh } from "./data/use-stale-refresh";
import { useAnnotations } from "./data/use-annotations";
import { MultiUploadDropZoneHost } from "./components/multi-upload-drop-zone-host/multi-upload-drop-zone-host";
import { useOmnibarFocusShortcut } from "./components/omnibar-host/use-omnibar-focus-shortcut";
import { StagingAccessGate } from "./components/staging-access-gate/staging-access-gate";
import { AppProvider, useApp } from "./state/app-context";
import { resetPinsLocalState, setPinsApiSyncEnabled } from "./state/pin-store";
import { pinnedStreamStore } from "./data/pinned-stream-store";
import "./app.scss";
// ---------------------------------------------------------------------------
// One-time localStorage migration: drop keys whose backing store has been
// deleted. Cheap to run on every boot (each `removeItem` is a single
// IndexedDB-less synchronous op) and harmless once the key is already gone,
// so we don't bother tracking a separate "migration applied" flag. Remove
// individual entries once enough time has passed that no existing user could
// still have the key (typically a release or two).
// ---------------------------------------------------------------------------
//
// - `viewer.authorFilter.v1` — author filter toggle store, removed when the
//   feature was dropped. Stranded in existing users' localStorage with no
//   reader.
try {
    // Guard so server-side rendering / non-browser test environments don't
    // throw on `window` access. The viewer ships as an SPA but the import
    // graph touches a few SSR-shaped modules during the build's pre-render
    // pass.
    if (typeof window !== "undefined") {
        window.localStorage.removeItem("viewer.authorFilter.v1");
    }
}
catch {
    // localStorage can throw in private-browsing / quota-exceeded states.
    // The shim is best-effort — a failure here just leaves the orphan key
    // in place, which is the same as not running the shim at all.
}
/**
 * Fallback UI shown when a render error is caught by Sentry.ErrorBoundary.
 * Receives resetError so the user can retry without a full reload when the error is transient.
 */
const ErrorFallback = ({ error, componentStack, resetError }) => (_jsxs("div", { style: { padding: "2rem", textAlign: "center", fontFamily: "system-ui" }, children: [_jsx("h2", { children: "Something went wrong" }), _jsx("p", { children: "An unexpected error occurred. You can try again or refresh the page." }), _jsxs("div", { style: {
                marginTop: "1rem",
                display: "flex",
                gap: "0.5rem",
                justifyContent: "center",
                flexWrap: "wrap",
            }, children: [_jsx("button", { onClick: resetError, style: { padding: "0.5rem 1rem" }, children: "Try again" }), _jsx("button", { onClick: () => window.location.reload(), style: { padding: "0.5rem 1rem" }, children: "Refresh" })] }), process.env.NODE_ENV !== "production" && (_jsxs("pre", { style: {
                marginTop: "1.5rem",
                textAlign: "left",
                fontSize: "12px",
                maxHeight: "300px",
                overflow: "auto",
                background: "#fff",
                border: "1px solid #ddd",
                padding: "1rem",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
            }, children: [error instanceof Error ? `${error.message}\n\n${error.stack}` : String(error), componentStack ? `\n\nComponent stack:${componentStack}` : ""] }))] }));
/**
 * Shared `onError` handler for Sentry.ErrorBoundary instances.
 *
 * Sentry.ErrorBoundary auto-captures via `captureReactException` in its
 * `componentDidCatch` BEFORE this callback fires — `eventId` is the id of
 * that auto-capture. We do two more things here that the auto-capture
 * alone doesn't give us:
 *
 *   1. An explicit, grep-able `Sentry.captureException(error)` from
 *      `app.tsx`. This file has had repeated regressions where the
 *      fallback UI renders but no Sentry event lands (NODE_ENV flag
 *      misconfigured, ad-blocker eating the POST, etc.). The explicit
 *      call gives dedupe a second shot at landing the event AND makes
 *      "this is the code that sends crashes to Sentry" obvious from
 *      grepping `Sentry.captureException` in this file rather than
 *      relying on `@sentry/react`'s internals. Sentry's
 *      `dedupeIntegration` (in default integrations) collapses the
 *      pair by fingerprint so the dashboard sees one event with our
 *      `section` tag merged in.
 *
 *   2. A `console.error` with the event id. When the dashboard stays
 *      empty (silent SDK failure: DSN blocked, CORS, dropped POST), the
 *      console line is the only in-browser diagnostic the user has to
 *      confirm "the boundary fired" without round-tripping to Sentry.
 *      `eventId` being a non-empty string means at least the
 *      auto-capture's `captureException` returned an id, narrowing the
 *      failure to the transport/network layer.
 */
const handleErrorBoundaryCatch = (section) => (error, componentStack, eventId) => {
    Sentry.withScope((scope) => {
        scope.setTag("section", section);
        scope.setTag("source", "react-error-boundary");
        if (componentStack) {
            // Cap componentStack length — runaway component trees can be
            // multi-KB. 2 KB is enough to identify the failing surface
            // without bloating the payload.
            scope.setContext("react", { componentStack: componentStack.slice(0, 2000) });
        }
        Sentry.captureException(error);
    });
    console.error(`[error-boundary:${section}] crash captured`, {
        eventId,
        error,
    });
};
/**
 * Sets up the QueryApi auth token provider using the auth context,
 * and identifies the user to Sentry + PostHog after login.
 * Must be rendered inside AuthProvider.
 */
const ApiSetup = ({ children }) => {
    const { getAccessToken, user } = useAuth();
    const app = useApp();
    const initialRouteEntity = useRouteEntity();
    // Tracks whether the user was ever signed in during this session, so the
    // homepage-pins local cache is only wiped on a real sign-out transition
    // (not on the initial uninitialized → logged_out render).
    const hadAuthenticatedUserRef = React.useRef(false);
    const sessionStartedFiredRef = React.useRef(false);
    useEffect(() => {
        setAuthTokenProvider(getAccessToken);
    }, [getAccessToken]);
    useEffect(() => {
        if (user) {
            Sentry.setUser({ id: user.id });
            identifyPosthog(user.id);
            if (!sessionStartedFiredRef.current) {
                sessionStartedFiredRef.current = true;
                const entry_kind = routeEntityToEntryKind(initialRouteEntity);
                const referrer_kind = referrerKindFromUrl(document.referrer);
                const utm = utmParamsFromSearch(window.location.search);
                registerEntrySuperProperties({ entry_kind, referrer_kind, ...utm });
                track({
                    name: "session.started",
                    props: { entry_kind, referrer_kind, ...utm, is_authenticated: true },
                });
            }
            app.setApiSyncEnabled(true);
            void app.hydratePreferencesFromApi();
            setPinsApiSyncEnabled(true);
            // Resolve pin order (API/localStorage) and prefetch pinned-block
            // envelopes into `blockStore`. Replaces the pin-prefetch path
            // that used to live inside `block-collection.ts`'s `queryFn`.
            void pinnedStreamStore.warm();
            hadAuthenticatedUserRef.current = true;
        }
        else {
            Sentry.setUser(null);
            resetPosthog();
            sessionStartedFiredRef.current = false;
            app.setApiSyncEnabled(false);
            app.clearPreferences();
            setPinsApiSyncEnabled(false);
            if (hadAuthenticatedUserRef.current) {
                resetPinsLocalState();
                // Drop the cached bootstrap + initial-blocks promises so the
                // next login fetches fresh — we never want one user's
                // hydrated state to leak into the next session. Gated on
                // `hadAuthenticatedUser` (i.e. real sign-out) because firing
                // `reset*` on the initial uninitialized → logged_out render
                // races against subscribers that have already started a fetch
                // — wiping a healthy inflight singleton mid-flight causes
                // the next caller to start a redundant second request.
                resetBootstrap();
                resetInitialBlocks();
                // Wipe the React Query cache + IDB envelope snapshot so a
                // logout-then-login-as-different-user can't briefly paint the
                // previous account's cards. Only fire on a real sign-out
                // transition so the initial uninitialized → logged_out render
                // doesn't pointlessly clear an already-empty cache.
                //
                // The homepage stream's persisted block-id snapshot has to
                // be cleared in the same beat — leaving it behind would let
                // the next mount's `hydrateStreamFromIdb` paint the
                // previous account's feed for ~20 ms (until the new
                // account's `listBlocks` lands and `ingestEnvelopes` merges
                // its own ids on top). Envelopes and ids are two halves of
                // the same fast-path snapshot; clear them together.
                //
                // The collection snapshot is cleared alongside — it feeds the
                // cross-layer `collectionCollection` cache that drives the
                // homepage synthetic-stack lookup, nav switcher, and "add to
                // collection" surfaces. Leaving it behind would briefly tile
                // the next account's homepage with the previous user's Group
                // / Stack identities until bootstrap reconciles.
                queryClient.clear();
                void clearBlockEnvelopesSnapshot();
                void clearStreamBlockIdsSnapshot();
                void clearCollectionsSnapshot();
                hadAuthenticatedUserRef.current = false;
            }
        }
        // initialRouteEntity is deliberately captured at first session.started
        // fire only — re-firing on route changes is handled by `route.viewed`.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, app]);
    return _jsx(_Fragment, { children: children });
};
/**
 * Gates the app based on auth status:
 * - uninitialized: show loading indicator
 * - logged_out: show login screen
 * - logged_in: show routes
 */
const AuthGate = () => {
    const { status } = useAuth();
    if (status === "uninitialized") {
        return (_jsxs("div", { className: "app-layout", children: [_jsx("aside", { className: "sidebar" }), _jsx("div", { className: "main-content", children: _jsx("div", { className: "loading-screen" }) })] }));
    }
    if (status === "logged_out") {
        return (_jsx("div", { className: "app-layout", children: _jsx("div", { className: "main-content", children: _jsx(LoginScreen, {}) }) }));
    }
    // status === "logged_in"
    return _jsx(LoggedInShell, {});
};
const LoggedInShell = () => {
    useStaleRefresh();
    // Root-level annotation runtime: clears the local annotation
    // collection on user-id transitions so a previous account's rows
    // can't bleed into the next session. Per-block subscriptions and
    // rendering remain nested inside each `BlockViewContainer`.
    useAnnotations();
    // ⌘D / ⌘F / ⌘E mounted here (not inside AppScreen) so the bindings
    // survive every route — including dev / components / experimental
    // screens that don't render the omnibar. When the bar isn't on the
    // current route the handler navigates home and queues focus via the
    // bridge, so the bar lights up the instant `<DraftBar>` registers
    // at the destination. See `use-omnibar-focus-shortcut.ts` for the
    // full rationale.
    useOmnibarFocusShortcut();
    return (_jsxs(_Fragment, { children: [_jsx(Routes, {}), _jsx(MultiUploadDropZoneHost, {})] }));
};
/**
 * Surface a "Reload to update" prompt the user can click when a new
 * service worker has finished installing in the background. Tapping
 * Reload posts `SKIP_WAITING` to the new SW, which activates and
 * triggers `controllerchange` below — that handler then reloads the
 * tab into the new bundle.
 *
 * Defensive `onClick` handler: by the time the user actually clicks,
 * the worker we captured at toast-creation time may have transitioned
 * out of `installed` (another tab triggered `forceRefreshCache`, a
 * newer SW superseded this one, etc.). `postMessage` to a `redundant`
 * worker is a silent no-op, so the user would click "Reload" and see
 * nothing happen. Falling back to a hard `location.reload()` covers
 * that case — the user wants new code, give them new code via the
 * most reliable path available.
 *
 * `duration: Infinity` keeps the toast pinned: `updatefound` only
 * fires once per registration lifecycle for a given new SW, so if the
 * user dismisses or misses the prompt they'd otherwise have no second
 * chance until the *next* deploy. The stable `id` lets repeat triggers
 * (e.g. a second updatefound from a long-lived session) update the
 * same pill in place instead of stacking duplicates.
 */
function promptForServiceWorkerUpdate(worker) {
    toast("New version available", {
        duration: Infinity,
        id: "sw-update-available",
        action: {
            label: "Reload",
            onClick: () => {
                if (worker.state === "installed") {
                    worker.postMessage("SKIP_WAITING");
                    return;
                }
                // Worker is no longer in `installed` (gone redundant, already
                // activated by another tab, etc.). `postMessage` would silently
                // drop, leaving the user stuck. Force a reload so the gesture
                // still does what they meant.
                window.location.reload();
            },
        },
    });
}
if ("serviceWorker" in navigator) {
    if (process.env.NODE_ENV === "production") {
        // Register on `load` (not at import time) so the SW install
        // doesn't compete for connection slots during the cold-load
        // critical path. The new SW will land in "waiting" state since
        // `sw.js` no longer calls `self.skipWaiting()` unconditionally —
        // user opts in via the toast below.
        window.addEventListener("load", () => {
            void (async () => {
                try {
                    const reg = await navigator.serviceWorker.register("/sw.js");
                    // A SW is already parked in `waiting` on cold boot when the
                    // previous session installed an update the user never clicked
                    // through. Re-surface the prompt so the new bundle can land.
                    if (reg.waiting && navigator.serviceWorker.controller) {
                        promptForServiceWorkerUpdate(reg.waiting);
                    }
                    reg.addEventListener("updatefound", () => {
                        // `reg.installing` is the new SW being installed. In the
                        // very rare case where the install completes between
                        // `updatefound` firing and this handler running (microtask
                        // race during a tab wake-up, say), `installing` is already
                        // null and the SW has slid into `waiting` — pick it up
                        // from there so we don't drop the prompt entirely.
                        const incoming = reg.installing ?? reg.waiting;
                        if (!incoming)
                            return;
                        incoming.addEventListener("statechange", () => {
                            // Only prompt on *update* (controller already exists),
                            // not on first-ever install — there's no current
                            // version to "update from" on a brand-new client, the
                            // new SW just silently takes over the next navigation.
                            if (incoming.state === "installed" &&
                                navigator.serviceWorker.controller) {
                                promptForServiceWorkerUpdate(incoming);
                            }
                        });
                    });
                }
                catch (e) {
                    // SW registration failure in production is rare but
                    // catastrophic (no shell precaching, no offline support, no
                    // update story) — surface to Sentry so it's actually
                    // observable instead of dying as a console line nobody
                    // reads. The `console.warn` stays for local debugging when
                    // a maintainer is running a prod build manually.
                    console.warn("[sw] register failed:", e);
                    Sentry.captureException(e, {
                        tags: { section: "service-worker", phase: "register" },
                    });
                }
            })();
        });
        navigator.serviceWorker.addEventListener("message", (event) => {
            if (event.data === "CACHE_CLEARED") {
                window.location.reload();
            }
        });
        // Reload once when the freshly-activated SW claims this tab. With
        // user-prompted updates above, `controllerchange` only fires after
        // the user clicked "Reload" in the toast — so the auto-reload
        // here is the second half of that gesture, not a surprise reload
        // mid-edit.
        //
        // Guarded by `didReloadForController` so a controller that
        // changes more than once (manual unregister + re-register, etc.)
        // can't drive a reload loop. Skipped when `controller` is null
        // at the time of the first registration, since that's the
        // initial install of the SW for this client — no stale page to
        // refresh into the new bundle.
        let didReloadForController = false;
        const hadControllerAtBoot = navigator.serviceWorker.controller !== null;
        navigator.serviceWorker.addEventListener("controllerchange", () => {
            if (didReloadForController)
                return;
            if (!hadControllerAtBoot)
                return;
            didReloadForController = true;
            window.location.reload();
        });
    }
    else {
        navigator.serviceWorker.getRegistrations().then((registrations) => {
            for (const r of registrations)
                r.unregister();
        });
    }
}
/** Post "FORCE_REFRESH" to the active service worker to wipe the cache and reload. */
export function forceRefreshCache() {
    navigator.serviceWorker.controller?.postMessage("FORCE_REFRESH");
}
if (process.env.NODE_ENV !== "production") {
    const devLabel = process.env.WORKTREE_NAME || "DEV";
    document.title = `[${devLabel}] ${document.title}`;
}
const container = document.getElementById("root");
if (container) {
    const root = ReactDOM.createRoot(container, {
        onUncaughtError: Sentry.reactErrorHandler(),
        onCaughtError: Sentry.reactErrorHandler(),
        onRecoverableError: Sentry.reactErrorHandler(),
    });
    root.render(_jsx(React.StrictMode, { children: _jsx(Sentry.ErrorBoundary, { fallback: ({ error, componentStack, eventId, resetError }) => (_jsx(ErrorFallback, { error: error, componentStack: componentStack, eventId: eventId, resetError: resetError })), showDialog: true, beforeCapture: (scope) => scope.setTag("section", "shell"), onError: handleErrorBoundaryCatch("shell"), children: _jsx(BrowserRouter, { children: _jsx(QueryClientProvider, { client: queryClient, children: _jsx(TooltipProvider, { delayDuration: 0, children: _jsx(StagingAccessGate, { children: _jsx(AuthProvider, { children: _jsx(AppProvider, { children: _jsx(ApiSetup, { children: _jsxs(Sentry.ErrorBoundary, { fallback: ({ error, componentStack, eventId, resetError }) => (_jsx(ErrorFallback, { error: error, componentStack: componentStack, eventId: eventId, resetError: resetError })), beforeCapture: (scope) => scope.setTag("section", "main"), onError: handleErrorBoundaryCatch("main"), children: [_jsx(AuthGate, {}), _jsx(Toaster, { position: "bottom-center", className: "viewer-toaster", 
                                                    // Base offsets are passed straight through (no
                                                    // `calc()` / `var()`). Sonner inlines these as
                                                    // `--offset-bottom` / `--mobile-offset-bottom`; our
                                                    // `.viewer-toaster` rule in `app.scss` overrides
                                                    // `bottom` directly with literal pixel targets per
                                                    // state so the focus-driven lift transitions work.
                                                    // Kept ~6px shy of the CSS resting target so the
                                                    // initial Sonner mount lands close to the final
                                                    // position before our rule wins on first paint.
                                                    offset: { bottom: 140 }, mobileOffset: { bottom: 136 }, visibleToasts: 3, 
                                                    // Pixels between adjacent toasts when the stack is
                                                    // expanded (hover-fan). Sonner's default is 14;
                                                    // tightened to 8 so the expanded stack reads as a
                                                    // unified column rather than three loose pills.
                                                    gap: 8, toastOptions: {
                                                        unstyled: true,
                                                        classNames: { toast: "viewer-toast" },
                                                        duration: 4000,
                                                    } })] }) }) }) }) }) }) }) }) }) }));
}
